"use client"

import { Command } from "cmdk"
import { useTheme } from "next-themes"
import { useRouter } from "next/navigation"
import { useEffect, useMemo, useState } from "react"
import {
  Activity,
  Bot,
  Building2,
  HeartPulse,
  Home,
  LogOut,
  Moon,
  Package,
  PanelLeftClose,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Smartphone,
  Store,
  Sun,
  Swords,
  Ticket,
  User,
  UserCog,
  Users2,
} from "lucide-react"

import { apiUrl, authHeaders, timeAgo } from "@/lib/api"
import { cn } from "@/lib/utils"
import { useAuth } from "@/contexts/auth-context"
import { useTenant } from "@/contexts/tenant-context"
import { useSidebar } from "@/contexts/sidebar-context"
import { useCommandPalette } from "@/contexts/command-palette-context"

interface CouponHit {
  id: number
  codigo: string
  status?: string
  discount_percentage?: number
  desconto?: string
  created_at?: string
  store_name?: string
  store?: { name?: string }
}

interface ProductHit {
  id: number
  titulo?: string
  nome?: string
  name?: string
  preco_atual?: string | number
  current_price?: string | number
  store_name?: string
  store?: { name?: string }
}

interface NavEntry {
  id: string
  label: string
  href: string
  icon: typeof Home
  keywords: string[]
  requiredFeature: string | null
  shortcut?: string
}

const NAV_ITEMS: NavEntry[] = [
  { id: "overview", label: "Visão geral", href: "/dashboard-cms", icon: Home, keywords: ["dashboard", "home", "overview", "inicio"], requiredFeature: null, shortcut: "1" },
  { id: "realtime", label: "Tempo real", href: "/dashboard-cms/realtime", icon: Activity, keywords: ["live", "agora", "realtime", "ao vivo"], requiredFeature: "realtime", shortcut: "2" },
  { id: "products", label: "Produtos", href: "/dashboard-cms/produtos", icon: Package, keywords: ["catalog", "produto"], requiredFeature: null, shortcut: "3" },
  { id: "stores", label: "Lojas", href: "/dashboard-cms/lojas", icon: Store, keywords: ["kabum", "terabyte", "lojas"], requiredFeature: null, shortcut: "4" },
  { id: "coupons", label: "Cupons", href: "/dashboard-cms/cupons", icon: Ticket, keywords: ["codigos", "desconto", "promocoes"], requiredFeature: null, shortcut: "5" },
  { id: "spy", label: "Spy", href: "/dashboard-cms/spy", icon: Swords, keywords: ["competidores", "vigilancia", "inteligencia"], requiredFeature: "spy", shortcut: "6" },
  { id: "health", label: "Saúde", href: "/dashboard-cms/saude", icon: HeartPulse, keywords: ["status", "sistema", "scrapers"], requiredFeature: "saude" },
  { id: "telegram", label: "Telegram", href: "/dashboard-cms/ferramentas/telegram", icon: Send, keywords: ["bot", "mensagens", "telegram"], requiredFeature: null },
  { id: "members", label: "Membros", href: "/dashboard-cms/equipe/membros", icon: Users2, keywords: ["equipe", "usuarios", "membros"], requiredFeature: "membros" },
]

const ADMIN_ITEMS: NavEntry[] = [
  { id: "admin-overview", label: "Painel admin", href: "/admin", icon: ShieldCheck, keywords: ["administracao"], requiredFeature: null },
  { id: "admin-tenants", label: "Tenants", href: "/admin/tenants", icon: Building2, keywords: ["clientes", "tenants"], requiredFeature: null },
  { id: "admin-users", label: "Usuários", href: "/admin/users", icon: UserCog, keywords: ["users", "membros"], requiredFeature: null },
]

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const RECENTS_KEY = "promobot_palette_recent_v1"
const RECENTS_MAX = 3

function readRecents(): string[] {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.slice(0, RECENTS_MAX) : []
  } catch {
    return []
  }
}

function pushRecent(id: string) {
  if (typeof window === "undefined") return
  try {
    const current = readRecents()
    const next = [id, ...current.filter((x) => x !== id)].slice(0, RECENTS_MAX)
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next))
  } catch {
    // localStorage quota / private mode — silent
  }
}

function getGreeting(name?: string): string {
  const hour = new Date().getHours()
  const prefix = hour < 5 ? "Boa madrugada" : hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite"
  const firstName = name ? name.split(/\s+/)[0].toLowerCase() : null
  return firstName ? `${prefix}, ${firstName}.` : `${prefix}.`
}

const PALETTE_EGG_KEY = "promobot_palette_signature_v1"
function firePaletteSignature() {
  if (typeof window === "undefined") return
  try {
    if (window.sessionStorage.getItem(PALETTE_EGG_KEY)) return
    window.sessionStorage.setItem(PALETTE_EGG_KEY, "1")
  } catch {
    return
  }
  console.log(
    "%cpaleta %c· ⌘K · busque, navegue, comande",
    "color: #c8821a; font: italic 20px 'Instrument Serif', Georgia, serif;",
    "color: #888; font: 11px ui-monospace, 'JetBrains Mono', monospace;",
  )
}

export default function CommandPalette() {
  const router = useRouter()
  const { open, setOpen } = useCommandPalette()
  const { user, logout } = useAuth()
  const { hasFeature } = useTenant()
  const { toggleMenuState } = useSidebar()
  const { theme, setTheme } = useTheme()

  const [query, setQuery] = useState("")
  const debouncedQuery = useDebounced(query, 220)
  const [coupons, setCoupons] = useState<CouponHit[]>([])
  const [products, setProducts] = useState<ProductHit[]>([])
  const [searching, setSearching] = useState(false)
  const [recentIds, setRecentIds] = useState<string[]>([])

  // Refresh recents whenever palette opens (catches changes from other tabs)
  useEffect(() => {
    if (open) {
      setRecentIds(readRecents())
      firePaletteSignature()
    }
  }, [open])

  // Clear query when palette closes
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setQuery("")
        setCoupons([])
        setProducts([])
      }, 180)
      return () => clearTimeout(t)
    }
  }, [open])

  // Numeric shortcut handlers (⌘1, ⌘2, …) — work even with palette closed
  useEffect(() => {
    const visibleNav = NAV_ITEMS.filter((item) => !item.requiredFeature || hasFeature(item.requiredFeature))
    const map = new Map<string, NavEntry>()
    for (const item of visibleNav) {
      if (item.shortcut) map.set(item.shortcut, item)
    }
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.shiftKey || e.altKey) return
      const target = e.target as HTMLElement | null
      const isEditable =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      if (isEditable) return
      const hit = map.get(e.key)
      if (!hit) return
      e.preventDefault()
      router.push(hit.href)
      setOpen(false)
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [hasFeature, router, setOpen])

  // Async search for coupons + products
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setCoupons([])
      setProducts([])
      setSearching(false)
      return
    }
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    if (!token) return

    let cancelled = false
    setSearching(true)

    Promise.all([
      fetch(`${apiUrl()}/coupons/?search=${encodeURIComponent(debouncedQuery)}&page_size=5`, {
        headers: authHeaders(token),
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch(`${apiUrl()}/products/?search=${encodeURIComponent(debouncedQuery)}&page_size=5`, {
        headers: authHeaders(token),
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ])
      .then(([couponData, productData]) => {
        if (cancelled) return
        const couponList: CouponHit[] = couponData?.results || (Array.isArray(couponData) ? couponData : [])
        const productList: ProductHit[] = productData?.results || (Array.isArray(productData) ? productData : [])
        setCoupons(couponList.slice(0, 5))
        setProducts(productList.slice(0, 5))
      })
      .finally(() => {
        if (!cancelled) setSearching(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  function runCommand(action: () => void, recordId?: string) {
    if (recordId) {
      pushRecent(recordId)
      setRecentIds((prev) => [recordId, ...prev.filter((x) => x !== recordId)].slice(0, RECENTS_MAX))
    }
    setOpen(false)
    setTimeout(action, 80)
  }

  const filteredNav = useMemo(
    () => NAV_ITEMS.filter((item) => !item.requiredFeature || hasFeature(item.requiredFeature)),
    [hasFeature],
  )

  const allNavMap = useMemo(() => {
    const map = new Map<string, NavEntry>()
    for (const n of [...filteredNav, ...(user?.is_superuser ? ADMIN_ITEMS : [])]) {
      map.set(n.id, n)
    }
    return map
  }, [filteredNav, user?.is_superuser])

  const recentEntries = useMemo(
    () => recentIds.map((id) => allNavMap.get(id)).filter((x): x is NavEntry => Boolean(x)),
    [recentIds, allNavMap],
  )

  const showWelcome = query.length === 0
  const showRecents = showWelcome && recentEntries.length > 0
  const greeting = getGreeting(user?.first_name || user?.username)
  const SUGGESTION_CHIPS = ["visão", "cupons", "tema", "spy"]

  if (!open) return null

  const isDark = theme === "dark"

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] animate-fade-in"
      style={{ animationDuration: "140ms" }}
    >
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <Command
        label="Paleta de comandos"
        shouldFilter
        className={cn(
          "relative w-full max-w-xl mx-4 rounded-2xl border border-panel-border bg-panel overflow-hidden",
          "shadow-float-strong",
        )}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault()
            setOpen(false)
          }
        }}
      >
        <div className="flex items-center gap-3 border-b border-panel-border px-4">
          <Search className="h-4 w-4 text-muted-foreground/80 flex-shrink-0" aria-hidden="true" />
          <Command.Input
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Pesquisar ou comandar..."
            // iOS Safari dá auto-zoom em qualquer input com font-size < 16px.
            // text-[16px] em mobile previne o zoom; sm: volta a 14px no desktop.
            className="flex h-12 w-full bg-transparent text-[16px] sm:text-sm text-foreground outline-none placeholder:text-muted-foreground/55"
          />
          <kbd className="font-mono text-[10px] tracking-wider text-muted-foreground/60 px-1.5 py-0.5 rounded bg-muted/40 border border-border/60">
            esc
          </kbd>
        </div>

        {showWelcome && (
          <div className="px-4 pt-3.5 pb-3 flex flex-wrap items-baseline justify-between gap-y-2 gap-x-4 border-b border-panel-border/60">
            <div className="font-display italic text-[17px] tracking-tight text-foreground/85">{greeting}</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/55">tente</span>
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => setQuery(chip)}
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted/40 border border-border/40 text-muted-foreground/80 hover:border-[var(--brand-amber)]/50 hover:text-[var(--brand-amber)] hover:bg-[var(--brand-amber)]/8 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        <Command.List className="max-h-[58vh] overflow-y-auto p-2 scrollbar-thin">
          <Command.Empty className="px-4 py-10 text-center text-sm text-muted-foreground">
            {searching ? "Buscando..." : `Nada encontrado para “${query}”.`}
          </Command.Empty>

          {/* Recentes — só quando vazio + tem histórico */}
          {showRecents && (
            <Command.Group heading="Recentes">
              {recentEntries.map((item) => {
                const Icon = item.icon
                return (
                  <Command.Item
                    key={`recent-${item.id}`}
                    value={`recente ${item.label} ${item.keywords.join(" ")}`}
                    onSelect={() => runCommand(() => router.push(item.href), item.id)}
                    className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
                  >
                    <Icon className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
                    <span className="flex-1">{item.label}</span>
                    <span className="text-[10px] text-muted-foreground/40 font-mono">↵</span>
                  </Command.Item>
                )
              })}
            </Command.Group>
          )}

          {/* Navegação */}
          <Command.Group heading="Navegação">
            {filteredNav.map((item) => {
              const Icon = item.icon
              return (
                <Command.Item
                  key={item.id}
                  value={`nav ${item.label} ${item.keywords.join(" ")}`}
                  onSelect={() => runCommand(() => router.push(item.href), item.id)}
                  className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
                >
                  <Icon className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
                  <span className="flex-1">{item.label}</span>
                  {item.shortcut && (
                    <kbd className="font-mono text-[10px] tracking-wider text-muted-foreground/60 px-1.5 py-0.5 rounded bg-muted/40 border border-border/40">
                      ⌘{item.shortcut}
                    </kbd>
                  )}
                </Command.Item>
              )
            })}
          </Command.Group>

          {/* Plataforma — só superuser */}
          {user?.is_superuser && (
            <Command.Group heading="Plataforma">
              {ADMIN_ITEMS.map((item) => {
                const Icon = item.icon
                return (
                  <Command.Item
                    key={item.id}
                    value={`admin ${item.label} ${item.keywords.join(" ")}`}
                    onSelect={() => runCommand(() => router.push(item.href), item.id)}
                    className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
                  >
                    <Icon className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
                    <span className="flex-1">{item.label}</span>
                  </Command.Item>
                )
              })}
            </Command.Group>
          )}

          {/* Ações */}
          <Command.Group heading="Ações">
            <Command.Item
              value="acao alternar tema dark light"
              onSelect={() => runCommand(() => setTheme(isDark ? "light" : "dark"))}
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
            >
              {isDark ? (
                <Sun className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              ) : (
                <Moon className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              )}
              <span className="flex-1">Alternar tema {isDark ? "claro" : "escuro"}</span>
            </Command.Item>
            <Command.Item
              value="acao recolher sidebar menu lateral"
              onSelect={() => runCommand(toggleMenuState)}
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
            >
              <PanelLeftClose className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              <span className="flex-1">Alternar barra lateral</span>
              <kbd className="font-mono text-[10px] tracking-wider text-muted-foreground/60 px-1.5 py-0.5 rounded bg-muted/40 border border-border/40">
                ⌘\
              </kbd>
            </Command.Item>
            <Command.Item
              value="acao perfil minha conta"
              onSelect={() => runCommand(() => router.push("/settings"))}
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
            >
              <User className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              <span className="flex-1">Configurações de conta</span>
            </Command.Item>
            <Command.Item
              value="acao telegram testar conexao bot"
              onSelect={() =>
                runCommand(() =>
                  router.push("/dashboard-cms/ferramentas/telegram?action=test-connection"),
                )
              }
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
            >
              <Bot className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              <span className="flex-1">Telegram · testar conexão do bot</span>
              <RefreshCw className="h-[12px] w-[12px] text-muted-foreground/40" aria-hidden="true" />
            </Command.Item>
            <Command.Item
              value="acao telegram adicionar destino canal grupo"
              onSelect={() =>
                runCommand(() =>
                  router.push(
                    "/dashboard-cms/ferramentas/telegram?action=focus-add-destination",
                  ),
                )
              }
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
            >
              <Send className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              <span className="flex-1">Telegram · adicionar destino</span>
              <Plus className="h-[12px] w-[12px] text-muted-foreground/40" aria-hidden="true" />
            </Command.Item>
            <Command.Item
              value="acao telegram reconfigurar monitor concorrentes telethon spy"
              onSelect={() =>
                runCommand(() =>
                  router.push(
                    "/dashboard-cms/ferramentas/telegram?action=reconfig-monitor",
                  ),
                )
              }
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
            >
              <Radio className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              <span className="flex-1">Telegram · reconfigurar monitor de concorrentes</span>
            </Command.Item>
            <Command.Item
              value="acao instalar app mobile celular pwa baixar smartphone novidade"
              onSelect={() => runCommand(() => router.push("/dashboard-cms/app"))}
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
            >
              <Smartphone className="h-[15px] w-[15px] text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
              <span className="flex-1">Instalar no celular</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--brand-amber)]">
                novidade
              </span>
            </Command.Item>
            <Command.Item
              value="acao sair logout"
              onSelect={() => runCommand(() => logout())}
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-rose-600 dark:text-rose-400 cursor-pointer aria-selected:bg-rose-500/10"
            >
              <LogOut className="h-[15px] w-[15px]" />
              <span className="flex-1">Sair</span>
            </Command.Item>
          </Command.Group>

          {/* Cupons */}
          {coupons.length > 0 && (
            <Command.Group heading="Cupons">
              {coupons.map((c) => {
                const discount = c.discount_percentage ? `-${c.discount_percentage}%` : c.desconto || ""
                const storeLabel = c.store?.name || c.store_name || ""
                return (
                  <Command.Item
                    key={`coupon-${c.id}`}
                    value={`cupom ${c.codigo} ${discount} ${storeLabel}`}
                    onSelect={() => runCommand(() => router.push(`/dashboard-cms/cupons?focus=${c.id}`))}
                    className="group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
                  >
                    <Ticket className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
                    <span className="font-mono text-[13px] font-medium text-foreground truncate">{c.codigo}</span>
                    {discount && (
                      <span className="font-mono text-[11px] text-[var(--brand-amber)] flex-shrink-0">{discount}</span>
                    )}
                    <span className="ml-auto flex items-center gap-2 flex-shrink-0 text-[11px]">
                      {storeLabel && <span className="text-muted-foreground/70 truncate max-w-[80px]">{storeLabel}</span>}
                      {c.created_at && <span className="font-mono text-muted-foreground/55">{timeAgo(c.created_at)}</span>}
                    </span>
                  </Command.Item>
                )
              })}
            </Command.Group>
          )}

          {/* Produtos */}
          {products.length > 0 && (
            <Command.Group heading="Produtos">
              {products.map((p) => {
                const title = p.titulo || p.nome || p.name || `Produto ${p.id}`
                const storeLabel = p.store?.name || p.store_name || ""
                const price = p.preco_atual || p.current_price
                return (
                  <Command.Item
                    key={`product-${p.id}`}
                    value={`produto ${title} ${storeLabel}`}
                    onSelect={() => runCommand(() => router.push(`/dashboard-cms/produtos?focus=${p.id}`))}
                    className="group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm text-muted-foreground cursor-pointer aria-selected:bg-muted/60 aria-selected:text-foreground"
                  >
                    <Package className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground/80 group-aria-selected:text-[var(--brand-amber)]" />
                    <span className="flex-1 truncate text-foreground">{title}</span>
                    <span className="flex items-center gap-2 flex-shrink-0 text-[11px]">
                      {storeLabel && <span className="text-muted-foreground/70 truncate max-w-[80px]">{storeLabel}</span>}
                      {price && <span className="font-mono text-muted-foreground">R$ {price}</span>}
                    </span>
                  </Command.Item>
                )
              })}
            </Command.Group>
          )}
        </Command.List>

        <div className="border-t border-panel-border px-3 py-2 flex items-center justify-between text-[10px] text-muted-foreground/60">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <kbd className="font-mono px-1 py-0.5 rounded bg-muted/40 border border-border/40">↑↓</kbd>
              navegar
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="font-mono px-1 py-0.5 rounded bg-muted/40 border border-border/40">↵</kbd>
              abrir
            </span>
          </div>
          <span className="font-display italic text-muted-foreground/70">paleta · ⌘K</span>
        </div>
      </Command>
    </div>
  )
}
